import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getConnectionStatus } from 'services/scheduleServices';
import ModalCreateEditEvent from 'views/Schedule/partials/ModalCreateEditEvent';
import outlook from '../../assets/images/Outlook-Icon-FY26.svg';
import './calendar.scss';
import usePermissionsAgenda from 'permissions/modules/agenda/usePermissionsAgenda';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { calendarComponentI18n } from './i18n';

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const getWeekDays = (locale) =>
  Array.from({ length: 7 }, (_, i) =>
    capitalize(
      new Date(2024, 0, 1 + i).toLocaleDateString(locale, { weekday: 'long' })
    )
  );

const getMonthNames = (locale) =>
  Array.from({ length: 12 }, (_, i) =>
    capitalize(
      new Date(2024, i, 1).toLocaleDateString(locale, { month: 'long' })
    )
  );

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function isSameDay(a, b) {
  if (!a || !b) return false;
  const da = startOfDay(a);
  const db = startOfDay(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

function buildCalendarMatrix(currentMonthDate) {
  const firstOfMonth = new Date(
    currentMonthDate.getFullYear(),
    currentMonthDate.getMonth(),
    1
  );
  const lastOfMonth = new Date(
    currentMonthDate.getFullYear(),
    currentMonthDate.getMonth() + 1,
    0
  );

  const firstDayWeekIndex = (firstOfMonth.getDay() + 6) % 7;

  const startDate = new Date(firstOfMonth);
  startDate.setDate(firstOfMonth.getDate() - firstDayWeekIndex);

  const weeks = [];
  let current = new Date(startDate);

  for (let week = 0; week < 6; week += 1) {
    const days = [];
    for (let i = 0; i < 7; i += 1) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    weeks.push(days);
  }

  return {
    weeks,
    firstOfMonth,
    lastOfMonth,
  };
}

const Calendar = ({
  DesignSystemComponents,
  initialDate,
  selectedDate,
  onDayClick,
  onMonthChange,
  renderDayContent,
  mode,
  onAppointmentCreated,
}) => {
  const { IconButton, Button, Dropdown, SvgImage } = DesignSystemComponents;

  const navigate = useNavigate();

  const { t, language: calendarLocale } = useScopedTranslation(calendarComponentI18n);

  const { canCreateAppointment, canConnectOutlook } = usePermissionsAgenda();

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const result = [];
    for (let y = currentYear - 5; y <= currentYear + 5; y += 1) {
      result.push(y);
    }
    return result;
  }, []);

  const today = useMemo(() => startOfDay(new Date()), []);
  const [currentMonth, setCurrentMonth] = useState(() =>
    startOfDay(initialDate || today)
  );
  const [internalSelectedDate, setInternalSelectedDate] = useState(null);
  const [outlookConnected, setOutlookConnected] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const isSelectedControlled = !!selectedDate;
  const effectiveSelectedDate = isSelectedControlled
    ? selectedDate && startOfDay(selectedDate)
    : internalSelectedDate;

  const weekDays = useMemo(() => getWeekDays(calendarLocale), [calendarLocale]);
  const monthNames = useMemo(() => getMonthNames(calendarLocale), [calendarLocale]);

  const { weeks } = useMemo(
    () => buildCalendarMatrix(currentMonth),
    [currentMonth]
  );

  const handleChangeMonth = (offset) => {
    const next = new Date(currentMonth);
    next.setMonth(currentMonth.getMonth() + offset);
    setCurrentMonth(next);
    onMonthChange?.(next);
  };

  const handleMonthSelectChange = (event) => {
    const monthIndex = parseInt(event.target.value, 10);
    if (Number.isNaN(monthIndex)) return;

    const next = new Date(currentMonth);
    next.setMonth(monthIndex);
    const normalized = startOfDay(next);
    setCurrentMonth(normalized);
    onMonthChange?.(normalized);
  };

  const handleYearSelectChange = (event) => {
    const year = parseInt(event.target.value, 10);
    if (Number.isNaN(year)) return;

    const next = new Date(currentMonth);
    next.setFullYear(year);
    const normalized = startOfDay(next);
    setCurrentMonth(normalized);
    onMonthChange?.(normalized);
  };

  const handleDayClick = (date) => {
    if (!isSelectedControlled) {
      setInternalSelectedDate(date);
    }
    onDayClick?.(date);
  };

  const monthOptions = monthNames.map((name, index) => ({
    label: name,
    value: index,
  }));
  const yearOptions = years.map((year) => ({
    label: String(year),
    value: year,
  }));

  const renderPrevButton = () => {
    if (IconButton) {
      return (
        <IconButton
          size='sm'
          icon='chevronLeft'
          onClick={() => handleChangeMonth(-1)}
        />
      );
    }

    return (
      <button
        type='button'
        className='calendar__nav-btn'
        onClick={() => handleChangeMonth(-1)}
      >
        ‹
      </button>
    );
  };

  const renderNextButton = () => {
    if (IconButton) {
      return (
        <IconButton
          size='sm'
          icon='chevronRight'
          onClick={() => handleChangeMonth(1)}
        />
      );
    }

    return (
      <button
        type='button'
        className='calendar__nav-btn'
        onClick={() => handleChangeMonth(1)}
      >
        ›
      </button>
    );
  };

  const renderTodayButton = () => {
    if (Button) {
      return (
        <Button
          size='sm'
          variant='ghost'
          onClick={() => {
            setCurrentMonth(today);
            onMonthChange?.(today);
          }}
        >
          {t('calendar_today')}
        </Button>
      );
    }

    return (
      <button
        type='button'
        className='calendar__today-btn'
        onClick={() => {
          setCurrentMonth(today);
          onMonthChange?.(today);
        }}
      >
        {t('calendar_today')}
      </button>
    );
  };

  const fetchOutlookConnection = async () => {
    try {
      const result = await getConnectionStatus.getStatus();
      setOutlookConnected(result);
    } catch (error) {
      console.warn('Error loading Outlook connection status:', error);
    }
  };

  useEffect(() => {
    fetchOutlookConnection();
  }, []);

  return (
    <>
      <div className='calendar'>
        <div className='d-flex justify-content-between align-items-center mb-5'>
          <div className='calendar__header d-flex align-items-center gap-4'>
            <div className='calendar__header-left d-flex align-items-center'>
              {renderPrevButton()}
            </div>
            <div className='calendar__header-center d-flex align-items-center gap-2'>
              {Dropdown ? (
                <Dropdown
                  options={monthOptions}
                  value={monthOptions.find(
                    (opt) => opt.value === currentMonth.getMonth()
                  )}
                  onChange={(selected) => {
                    if (!selected) return;
                    const next = new Date(currentMonth);
                    next.setMonth(selected.value);
                    const normalized = startOfDay(next);
                    setCurrentMonth(normalized);
                    onMonthChange?.(normalized);
                  }}
                />
              ) : (
                <select
                  className='calendar__month-select form-select form-select-sm'
                  value={currentMonth.getMonth()}
                  onChange={handleMonthSelectChange}
                >
                  {monthNames.map((name, index) => (
                    <option key={name} value={index}>
                      {name}
                    </option>
                  ))}
                </select>
              )}

              {Dropdown ? (
                <Dropdown
                  options={yearOptions}
                  value={yearOptions.find(
                    (opt) => opt.value === currentMonth.getFullYear()
                  )}
                  onChange={(selected) => {
                    if (!selected) return;
                    const next = new Date(currentMonth);
                    next.setFullYear(selected.value);
                    const normalized = startOfDay(next);
                    setCurrentMonth(normalized);
                    onMonthChange?.(normalized);
                  }}
                />
              ) : (
                <select
                  className='calendar__year-select form-select form-select-sm'
                  value={currentMonth.getFullYear()}
                  onChange={handleYearSelectChange}
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className='calendar__header-right d-flex align-items-center gap-2'>
              {renderNextButton()}
              {renderTodayButton()}
            </div>
          </div>
          {mode === 'appointments' && (
            <>
              <div className='d-flex gap-3 align-items-center'>
                {outlookConnected.connected === false ? (
                  <Button
                    label={t('calendar_connect_outlook')}
                    onClick={() => navigate('/mi-perfil')}
                    variant='secondary'
                    size='sm'
                    disabled={!canConnectOutlook}
                  />
                ) : (
                  <div title={t('calendar_connected_outlook')}>
                    <SvgImage className='outlook-logo' src={outlook} />
                  </div>
                )}
                <Button
                  label={t('calendar_new_event')}
                  size='sm'
                  variant='primary'
                  iconStart='plusLg'
                  onClick={() => setShowCreate(true)}
                  disabled={!canCreateAppointment}
                />
              </div>
            </>
          )}
        </div>
        <div className='calendar-wrapper'>
          <div className='calendar__weekdays d-grid'>
            {weekDays.map((day) => (
              <div key={day} className='calendar__weekday'>
                {day}
              </div>
            ))}
          </div>
          <div className='calendar__grid d-grid'>
            {weeks.map((week, weekIndex) =>
              week.map((date, dayIndex) => {
                const isToday = isSameDay(date, today);
                const isSelected = isSameDay(date, effectiveSelectedDate);
                const isOutsideMonth =
                  date.getMonth() !== currentMonth.getMonth();
                const isWeekend = date.getDay() === 0 || date.getDay() === 6;

                const cellClassNames = [
                  'calendar__cell',
                  isToday && 'calendar__cell--today',
                  isSelected && 'calendar__cell--selected',
                  isOutsideMonth && 'calendar__cell--outside',
                  isWeekend && 'calendar__cell--weekend',
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <button
                    key={`${weekIndex}-${dayIndex}`}
                    type='button'
                    className={cellClassNames}
                    onClick={() => handleDayClick(date)}
                  >
                    <div className='calendar__cell-header'>
                      <span className='calendar__date-number'>
                        {date.getDate()}
                      </span>
                    </div>
                    <div className='calendar__cell-content'>
                      {(() => {
                        const content = renderDayContent
                          ? renderDayContent(date)
                          : null;

                        if (Array.isArray(content)) {
                          const visible = content.slice(0, 3);
                          const hiddenCount = content.length - visible.length;

                          return (
                            <>
                              {visible}
                              {hiddenCount > 0 && (
                                <div className='calendar__more-tasks mini fw-bold'>
                                  {t('calendar_more_items', { count: hiddenCount })}
                                </div>
                              )}
                            </>
                          );
                        }

                        return content;
                      })()}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
      <ModalCreateEditEvent
        DesignSystemComponents={DesignSystemComponents}
        show={showCreate}
        onClose={() => setShowCreate(false)}
        today={today}
        onCreated={onAppointmentCreated}
      />
    </>
  );
};

export default Calendar;
